'use client'

import { useRef, useState, useEffect } from 'react'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { ChevronUp, Eye, EyeClosed, ChevronDown } from 'lucide-react'

type MobileLoginFormProps = {
  nationid: string
  password: string
  setNationid: (value: string) => void
  setPassword: (value: string) => void
  handleSubmit: (e: React.FormEvent) => void
  isPending: boolean
  isError: boolean
  mutationError: unknown
}

const CLOSED_HEIGHT = 120
const OPEN_HEIGHT = 350

export const MobileLoginForm = ({
  nationid,
  password,
  setNationid,
  setPassword,
  handleSubmit,
  isPending,
  isError,
  mutationError,
}: MobileLoginFormProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showPass, setShowPass] = useState(false)

  const startY = useRef(0)
  const startHeight = useRef(CLOSED_HEIGHT)
  const rawHeight = useMotionValue(CLOSED_HEIGHT)

  const height = useSpring(rawHeight, {
    stiffness: 300,
    damping: 35,
    mass: 0.7,
  })


  const backdropBlur = useTransform(
    height,
    [CLOSED_HEIGHT, OPEN_HEIGHT],
    ['blur(0px)', 'blur(3px)']
  )

  const contentOpacity = useTransform(
    height,
    [OPEN_HEIGHT - 150, OPEN_HEIGHT - 30],
    [0, 1]
  )



  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    startY.current = event.clientY
    startHeight.current = rawHeight.get()
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
    const distance = startY.current - event.clientY
    const nextHeight = Math.min(
      OPEN_HEIGHT,
      Math.max(CLOSED_HEIGHT, startHeight.current + distance)
    )
    rawHeight.set(nextHeight)
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    const currentHeight = rawHeight.get()
    const shouldOpen = currentHeight > (CLOSED_HEIGHT + OPEN_HEIGHT) / 2
    setIsOpen(shouldOpen)
    rawHeight.set(shouldOpen ? OPEN_HEIGHT : CLOSED_HEIGHT)
  }

  const handleOpen = () => {
    setIsOpen(true)
    rawHeight.set(OPEN_HEIGHT)
  }


  const handleClose = () => {
    setIsOpen(false)
    rawHeight.set(CLOSED_HEIGHT)
  }

  return (

    <div className="relative h-[100dvh] w-full overflow-hidden bg-neutral-300 touch-none">
      <div className="flex h-[30dvh] items-center justify-center">
        <h1 className="text-3xl font-extrabold text-neutral-800">
          Platform
        </h1>
      </div>

      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <img
          src="https://rosetejarat.com/wp-content/uploads/2025/03/b18e627e-6083-43b1-836c-18a7d29ce631-1.gif"
          alt=""
          className="w-[260px] select-none opacity-80 mix-blend-multiply"
        />
      </div>


      <motion.div
        style={{
          backdropFilter: backdropBlur,
          WebkitBackdropFilter: backdropBlur,
        }}
        onClick={handleClose}
        className={`absolute inset-0 z-20 bg-transparent ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'
          }`}
      />

      <motion.div
        animate={{
          y: isOpen ? 0 : [0, -9, 0],
          opacity: isOpen ? 0 : [0.45, 1, 0.45],
        }}
        transition={{
          y: { duration: 1.25, repeat: isOpen ? 0 : Infinity, ease: 'easeInOut' },
          opacity: { duration: 1.25, repeat: isOpen ? 0 : Infinity, ease: 'easeInOut' },
        }}
        className="pointer-events-none absolute bottom-[180x] left-1/2 z-40 "
      >
        {/* <ChevronUp size={34} strokeWidth={3} /> */}
      </motion.div>

      <motion.div
        style={{ height }}
        className="absolute bottom-6 left-5 right-5 z-30 overflow-hidden rounded-[34px] bg-neutral-800 px-7 pb-6 pt-4 shadow-[0_14px_45px_rgba(0,0,0,0.35)]"
      >
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onClick={() => {
            if (isOpen) {
              handleClose()
            } else {
              handleOpen()
            }
          }}
          className="flex h-8 touch-none cursor-grab items-start justify-center active:cursor-grabbing -translate-y-1/2 text-neutral-400"
        >
          {isOpen ? (
            <ChevronDown size={34} strokeWidth={3} />
          ) : (
            <ChevronUp size={34} strokeWidth={3} />
          )}
        </div>


        <form onSubmit={handleSubmit} className="login-form relative h-[calc(100%-30px)] ">
          <motion.div
            style={{
              opacity: contentOpacity,
              pointerEvents: isOpen ? 'auto' : 'none',
            }}
            className="space-y-8 pt-5"
          >

            <div className="group">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-neutral-500 transition-colors group-focus-within:text-white">
                Nation ID
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={nationid}
                onChange={(event) => setNationid(event.target.value)}
                disabled={isPending}
                className={`w-full border-0 border-b border-neutral-700 bg-transparent pb-3 text-base  outline-none focus:border-white 
                  ${isOpen
                    ? 'text-white autofill-visible'
                    : 'text-transparent'
                  }`}
              />
            </div>


            <div className="group">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-neutral-500 transition-colors group-focus-within:text-white">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={isPending}
                  className={`w-full border-0 border-b border-neutral-700 bg-transparent pb-3 pr-10 text-base text-white outline-none focus:border-white
                    ${isOpen
                      ? 'text-white autofill-visible'
                      : 'text-transparent'
                    }`}

                />
                <button
                  type="button"
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={() => setShowPass((previous) => !previous)}
                  className="absolute right-0 top-0 flex h-8 w-8 items-center justify-center text-neutral-500 transition-colors group-focus-within:text-white"
                >
                  {showPass ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeClosed className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

          </motion.div>


          <div className="absolute bottom-0 left-0 w-full bg-neutral-800">
            <button
              type="submit"
              disabled={isPending}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                if (!isOpen) {
                  event.preventDefault()
                  handleOpen()
                }
              }}
              className="h-14 w-full rounded-2xl bg-neutral-300 font-bold text-neutral-800 transition-transform active:scale-[0.98] disabled:opacity-50"
            >
              {isPending ? 'Logging in...' : 'Login'}
            </button>

          </div>
        </form>
      </motion.div>
    </div>
  )
}
